<template>
  <div>
    <div class="mb-6 pb-6 border-b-2 border-grey-light flex flex-wrap items-center">
      <div>
        <h1 class="text-grey-darker text-xl font-semibold leading-normal uppercase">
          Custom Print Orders
        </h1>
        <a
          @click="$router.push('/print-order-form')"
          class="text-grey-dark text-sm font-medium leading-normal cursor-pointer no-underline hover:underline">
          &lt; Order Another Product
        </a>
      </div>
    </div>

    <loader v-if="!loaded" />

    <!-- OFFICE HAS NO ACCESS -->
    <panel v-else-if="!officeAccess" class="pt-6">
      <strong class="mb-2">Access Denied</strong>
      <p>Your office does not currently have access to use the print order form.</p>
    </panel>

    <div v-else class="rounded-lg bg-white relative flex flex-col p-6 overflow-x-auto">
      <h3 class="pb-6 leading-none"><i class="fas fa-list mr-2 text-lg" /> Order History</h3>
      <div class="flex flex-wrap items-center leading-none pb-6 border-b-2 border-grey-light">
        <div class="flex w-full mb-4 lg:w-1/3 lg:mb-0">
          <div class="flex flex-row w-full p-3 rounded border border-grey-light bg-white ">
            <input type="text" v-model="searchParams.q" placeholder="Search" class="flex-grow pr-3 border-r border-grey-light" />
            <i class="fa fa-search text-grey pl-3"></i>
          </div>
        </div>
        <div class="w-full lg:w-2/3">
          <print-sub-menu />
        </div>
      </div>

      <div v-if="orders.length === 0" class="py-6">
        <p>There are no orders to display. <a class="underline cursor-pointer" @click="$router.push('/print-order-form')">Continue shopping</a></p>
      </div>

      <table v-else class="w-full mb-8 leading-normal">
        <thead>
          <tr class="text-sm uppercase leading-tight">
            <th class="text-left font-semibold pl-6 pr-4 py-4">Order No.</th>
            <th class="text-center font-semibold p-4">Order Summary</th>
            <th class="text-center font-semibold p-4">Order Placed</th>
            <th class="text-center font-semibold p-4">Status</th>
            <th class="text-center font-semibold p-4">Cost</th>
            <th class="text-center font-semibold p-4">Fulfilment</th>
            <th class="text-center font-semibold p-4">Proof Document</th>
            <th class="text-center font-semibold p-4">Re-order</th>
            <th class="text-center font-semibold pl-4 pr-6 py-4">Review</th>
            <th class="text-center font-semibold pl-4 pr-6 py-4">
              Payment Link
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(order, i) in orders">
            <tr :key="i" class="border-t border-b border-grey-light text-sm">
              <td class="pl-6 pr-4 py-4 border-l-8 border-grey whitespace-no-wrap">{{ order.id }}<i v-if="order.items.length > 1" @click="order.viewMore = !order.viewMore" class="fas fa-chevron-down ml-5 text-grey cursor-pointer"></i></td>
              <td class="text-center p-4">
                <p v-if="order.items.length > 1" @click="order.viewMore = true" class="font-semibold cursor-pointer hover:underline">{{ order.items.length + ' Items' }}</p>
                <p v-else class="font-semibold">{{ order.items.length === 1 ? '1 Item' : order.items.length + ' Items' }}</p>
                <p v-if="order.items.length === 1" @click="viewDetails(order.items[0])" class="cursor-pointer hover:underline">{{ order.items[0].sale_item.category.name + ' - ' + order.items[0].sale_item.name + ' x ' + order.items[0].quantity }}</p>
                <p v-if="order.items.length === 1 && order.items[0].customer_reference">Reference: <em>{{ order.items[0].customer_reference }}</em></p>
              </td>
              <td class="text-center p-4">
                {{ order.created_at | date }}
                <small class="text-grey-darker block">{{ order.created_at | time }}</small>
              </td>
              <td class="text-center p-4 whitespace-no-wrap">
                <span v-if="order.items.length === 1" :class="calculatedStatusColour(order.items[0], order)"><small><i class="fas fa-circle pr-1" /></small> {{ calculatedStatus(order.items[0], order) }}</span>
              </td>
              <td class="text-center font-semibold p-4">
                <span v-if="order.office.network_id === 8">
                  {{ parseFloat(order.cost_total) | gbp }}
                </span>
                <span v-else>
                  {{ parseFloat(order.cost_subtotal) | gbp }} +VAT
                </span>
              </td>
              <td class="text-center p-4">
                <p v-if="order.items.length === 1" :class="calculatedFulfilmentStatusColour(order.items[0])">{{ calculatedFulfilmentStatus(order.items[0]) }}</p>
                <p v-if="order.items.length === 1" class="text-xs whitespace-no-wrap" :class="calculatedFulfilmentStatusColour(order.items[0])">{{ calculatedFulfilmentStatusTime(order.items[0]) }}</p>
              </td>
              <td class="text-center p-4">
                <span v-if="order.items[0].is_stock_item"></span>
                <i v-else-if="order.items.length === 1 && getProofUrl(order.items[0])" class="fas fa-download text-xl text-blue cursor-pointer" @click="openUrl(getProofUrl(order.items[0]))" />
                <span v-else-if="order.items.length === 1">Pending</span>
              </td>
              <td class="text-center p-4 whitespace-no-wrap">
                <div v-if="order.items.length === 1">
                  <loader v-if="reordering" />
                  <i v-else @click="reOrder(order.items[0])" class="tooltip-container fas fa-cart-plus text-xl text-blue cursor-pointer">
                    <div class="absolute border border-bg-primary p-2 bg-grey-darker shadow z-10 rounded text-xs normal-case mt-1 tooltip-msg pointer-events-none">
                      <span class="text-white font-sans leading-normal font-normal whitespace-normal">
                        Re-order these items.<br />
                        Add it to my basket
                      </span>
                    </div>
                  </i>
                </div>
              </td>
              <td class="text-center p-4 whitespace-no-wrap">
                <i v-if="order.items.length === 1" @click="viewDetails(order.items[0])" class="fas fa-clipboard-list text-3xl text-grey cursor-pointer" />
              </td>
              <td class="text-center pl-4 pr-6 py-4 border-r-8 border-grey">
                <button type="button" v-if="!checkPaymentStatus(order)" @click="payForOrder(order.id)" class="bg-grey-darker text-white py-1 px-1 mx-2 rounded text-xs whitespace-no-wrap">
                  Pay Here
                </button>
                <span v-else>-</span>
              </td>
            </tr>
            <template v-if="order.items.length > 1 && order.viewMore === true">
              <tr v-for="(item, j) in order.items" :key="((i+1)*100)+j" class="border-t border-b border-grey-light text-sm">
                <td class="pl-6 pr-4 py-4 border-l-8 border-grey-lighter whitespace-no-wrap"><i class="fas fa-minus mr-3 text-grey"></i>{{ order.id + '-' + (j + 1) }}</td>
                <td class="text-center p-4">
                  <p @click="viewDetails(item)" class="cursor-pointer hover:underline">{{ item.sale_item.category.name + ' - ' + item.sale_item.name + ' x ' + item.quantity }}</p>
                  <p v-if="item.customer_reference">Reference: <em>{{ item.customer_reference }}</em></p>
                </td>
                <td class="text-center p-4">
                  {{ order.created_at | date }}
                  <small class="text-grey-darker block">{{ order.created_at | time }}</small>
                </td>
                <td class="text-center p-4 whitespace-no-wrap">
                  <span :class="calculatedStatusColour(item, order)"><small><i class="fas fa-circle pr-1" /></small> {{ calculatedStatus(item, order) }}</span>
                </td>
                <td class="text-center font-semibold p-4">
                  <span v-if="order.office.network_id === 8">
                    {{ parseFloat(item.cost_total) | gbp }}
                  </span>
                  <span v-else>
                    {{ parseFloat(item.cost_subtotal) | gbp }} +VAT
                  </span>
                </td>
                <td class="text-center p-4">
                  <p :class="calculatedFulfilmentStatusColour(item)">{{ calculatedFulfilmentStatus(item) }}</p>
                  <p class="text-xs whitespace-no-wrap" :class="calculatedFulfilmentStatusColour(item)">{{ calculatedFulfilmentStatusTime(item) }}</p>
                </td>
                <td class="text-center p-4">
                  <span v-if="item.is_stock_item"></span>
                  <i v-else-if="getProofUrl(item)" class="fas fa-download text-xl text-blue cursor-pointer" @click="openUrl(getProofUrl(item))" />
                  <span v-else>Pending</span>
                </td>
                <td class="text-center p-4 whitespace-no-wrap">
                  <loader v-if="reordering" />
                  <i v-else @click="reOrder(item)" class="tooltip-container fas fa-cart-plus text-xl text-blue cursor-pointer">
                    <div class="absolute border border-bg-primary p-2 bg-grey-darker shadow z-10 rounded text-xs normal-case mt-1 tooltip-msg pointer-events-none">
                      <span class="text-white font-sans leading-normal font-normal whitespace-normal">
                        Re-order these items.<br />
                        Add it to my basket
                      </span>
                    </div>
                  </i>
                </td>
                <td class="text-center p-4 whitespace-no-wrap">
                  <i @click="viewDetails(item)" class="fas fa-clipboard-list text-3xl text-grey cursor-pointer" />
                </td>
                <td class="text-center pl-4 pr-6 py-4 border-r-8 border-grey-lighter"></td>
              </tr>
            </template>
          </template>
        </tbody>
      </table>
      <paginator :data="pagination" @change="pageChanged" />
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { v2Api } from '@/http'
import { isEmpty } from 'lodash'
import { mapState, mapActions } from 'vuex'
import PrintSubMenu from './print-order-form/PrintSubMenu'
export default {
  components: {
    PrintSubMenu
  },
  data () {
    return {
      loaded: false,
      orders: [],
      officeAccess: true,
      searchParams: {
        q: null,
        office_id: null,
        order_by: 'created_at',
        sort: 'desc',
        page: 1,
        trashed: 0
      },
      pagination: {
        current_page: 1,
        last_page: null
      },
      reordering: false
    }
  },
  computed: {
    ...mapState(['selectedOffice', 'profile'])
  },
  watch: {
    selectedOffice: {
      handler () {
        this.loadPage()
      },
      deep: true
    },
    searchParams: {
      deep: true,
      handler () {
        this.search()
      }
    }
  },
  created () {
    this.searchParams.office_id = this.selectedOffice.id
    this.loadPage()
  },
  methods: {
    isEmpty,
    ...mapActions([
      'displayToastNotification'
    ]),
    loadPage () {
      this.loaded = false
      Promise.all([
        this.search()
      ]).then(() => {
        this.loaded = true
      })
    },
    search () {
      return v2Api.get('custom-print-orders', {
        params: this.searchParams
      }).then((response) => {
        this.orders = response.data.orders.data
        this.pagination = {
          current_page: response.data.orders.current_page,
          last_page: response.data.orders.last_page
        }
        this.setViewMoreDefaults()
      }).catch(() => {
        this.officeAccess = false
        this.loaded = true
      })
    },
    getProofUrl (item) {
      if (!item.attachments || item.attachments.length === 0) {
        return false
      }

      for (let i = 0; i < item.attachments.length; i++) {
        if (item.attachments[i].is_proof) {
          return item.attachments[i].url
        }
      }

      return false
    },
    reOrder (item) {
      this.reordering = true
      v2Api.post(`/custom-print-order-items/${item.id}/re-order`)
        .then(response => {
          this.$router.push(`/print-order-form/saved-items/${response.data.id}`)
        }, (errors) => {
          this.displayToastNotification({
            type: 'danger',
            icon: 'fa-exclamation',
            title: item ? 'Error re-ordering item' : 'Error re-ordering items',
            info: errors.response.data.message
          })
        }).finally(() => {
          this.reordering = false
        })
    },
    viewDetails (item) {
      this.$router.push(`/print-order-form/order-details/${item.id}`)
    },
    setViewMoreDefaults () {
      this.orders.forEach(order => {
        this.$set(order, 'viewMore', false)
      })
    },
    calculatedStatus (item, order) {
      if (order.payment_type_id === 1 && (order.payment && !order.payment.stripe_payment_id)) {
        return 'Payment Pending'
      }
      if (item.deleted_at || order.status_id === 5) {
        return 'Cancelled'
      }
      if (item.completed_at) {
        return 'Completed'
      }
      if (item.approved_at) {
        return 'Approved'
      }
      if (item.approval_requested_at) {
        return 'Requires Approval'
      }
      if (item.scheduled_at) {
        return 'Scheduled'
      }

      return 'Pending'
    },
    calculatedStatusColour (item, order) {
      if (order.payment_type_id === 1 && (order.payment && !order.payment.stripe_payment_id)) {
        return 'text-red'
      }
      if (item.deleted_at || order.status_id === 5) {
        return 'text-grey-dark'
      }
      if (item.completed_at || item.approved_at) {
        return 'text-green'
      }
      if (item.approval_requested_at) {
        return 'text-red'
      }

      return 'text-blue'
    },
    calculatedFulfilmentStatus (item) {
      if (item.is_stock_item) {
        return ''
      }
      if (item.sent_to_print_at) {
        return 'Sent to Print'
      }
      if (item.approved_at) {
        return 'Approved'
      }
      if (item.approval_requested_at) {
        return 'Artwork Uploaded'
      }
      if (item.scheduled_at) {
        return 'Scheduled'
      }

      return ''
    },
    calculatedFulfilmentStatusTime (item) {
      if (item.is_stock_item) {
        return ''
      }
      if (item.sent_to_print_at) {
        return Vue.filter('datetime')(item.sent_to_print_at)
      }
      if (item.approved_at) {
        return Vue.filter('datetime')(item.approved_at)
      }
      if (item.approval_requested_at) {
        return Vue.filter('datetime')(item.approval_requested_at)
      }
      if (item.scheduled_at) {
        return Vue.filter('datetime')(item.scheduled_at)
      }

      return ''
    },
    calculatedFulfilmentStatusColour (item) {
      if (item.completed_at || item.approved_at) {
        return 'text-green'
      }

      return ''
    },
    pageChanged (page) {
      this.searchParams.page = page
    },
    openUrl (url) {
      window.open(url, '_blank')
    },
    checkPaymentStatus (order) {
      if (order.payment_type_id === 1 && (order.payment && !order.payment.stripe_payment_id)) {
        return false
      }
      return true
    },
    payForOrder (orderId) {
      v2Api.get(`/custom-print-orders/${orderId}/start-payment`).then((response) => {
        if (response.data.paid) {
          this.displayToastNotification({
            type: 'success',
            icon: 'fa-check',
            title: 'Successfully Sent',
            info: `Payment has been already done for this order #${orderId} `
          })
        } else if (response.data.checkout_url) {
          window.location = response.data.checkout_url
        } else {
          this.displayToastNotification({
            type: 'danger',
            icon: 'fa-times-circle',
            title: 'Internal Server Error',
            info: 'Whoops! Looks like something went wrong.'
          })
        }
      }, (errors) => {
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Internal Server Error',
          info: 'Whoops! Looks like something went wrong.'
        })
      })
    }
  }
}
</script>
